/**
 * Stock Issue Service — ใบเบิก / ยืมอุปกรณ์ที่ไม่ผูกกับการเช่าของลูกค้า (Phase 2)
 *
 * ตอบโจทย์ที่ระบบเดิมบันทึกไม่ได้เลย: "dev เบิกเครื่องไปทดสอบ / QA เบิกกระดาษ / สำรองติดรถ"
 * — `accessory_requests` ทำไม่ได้เพราะบังคับ `rental_id` NOT NULL + รับเฉพาะของนับจำนวน
 * ใบเบิกนี้ `rental_id` เป็น optional (อ้างอิงเฉย ๆ) จึงยุบโมดูลนั้นได้
 *
 * **ไม่มีขั้นอนุมัติ** (PO 2026-09-07) — สร้างใบ = ตัดสต็อกทันทีผ่าน `stock_service`
 *
 * สิทธิ์ (ตรงกับ backend `endpoints/stock_issues.py`):
 * - อ่าน → `stock_issues.view`
 * - สร้างใบ → `stock_issues.create`
 * - คืน / ตัดจำหน่าย / ยกเลิก → `stock_issues.create` **หรือ** `stock_issues.edit`
 * - export → (`stock_issues.export` หรือ `exports.view`) และต้องมี view ด้วย
 */
import ApiWithLogging from '../utils/apiWithLogging';
import { PickerBlockedItem } from '../types/rentalHold';

// ========== Enum (mirror backend models/stock_issue.py) ==========

export type StockIssuePurpose =
  | 'INTERNAL_TEST'
  | 'DEMO'
  | 'SPARE_ONSITE'
  | 'MAINTENANCE_USE'
  | 'CONSUMABLE'
  | 'CUSTOMER_OUT_OF_CONTRACT'
  | 'WRITE_OFF'
  | 'OTHER';

export type StockIssueStatus =
  | 'ISSUED'
  | 'PARTIALLY_RETURNED'
  | 'RETURNED'
  | 'WRITTEN_OFF'
  | 'CANCELLED';

/** `EquipmentCondition` ฝั่ง backend (ใช้ร่วมกับใบตามเก็บคืน) */
export type StockReturnCondition = 'GOOD' | 'DAMAGED' | 'LOST';

export interface EnumOption {
  value: string;
  label: string;
}

/** GET /stock-issues/purposes — แหล่งความจริงของคำไทยทั้ง 3 ชุด */
export interface StockIssueOptionsResponse {
  purposes: EnumOption[];
  statuses: EnumOption[];
  conditions: EnumOption[];
}

/**
 * ค่าคงที่สำรอง — ใช้เมื่อ `GET /stock-issues/purposes` ยังไม่ตอบ (หน้าจอโหลดพร้อมกัน)
 * หรือเป็น fallback ของ `*_label` ที่ backend ส่งมาอยู่แล้วในทุกแถว
 * **ห้ามใช้แทนค่าจาก API ถ้ามีค่าจาก API แล้ว**
 */
export const STOCK_ISSUE_PURPOSES: ReadonlyArray<{ value: StockIssuePurpose; label: string }> = [
  { value: 'INTERNAL_TEST', label: 'ทดสอบภายใน' },
  { value: 'DEMO', label: 'สาธิต/นำเสนอลูกค้า' },
  { value: 'SPARE_ONSITE', label: 'สำรองหน้างาน' },
  { value: 'MAINTENANCE_USE', label: 'ใช้ในงานซ่อม' },
  { value: 'CONSUMABLE', label: 'ใช้สิ้นเปลือง' },
  { value: 'CUSTOMER_OUT_OF_CONTRACT', label: 'ส่งให้ลูกค้านอกสัญญา' },
  { value: 'WRITE_OFF', label: 'ตัดจำหน่าย' },
  { value: 'OTHER', label: 'อื่นๆ' },
];

export const STOCK_ISSUE_STATUSES: ReadonlyArray<{ value: StockIssueStatus; label: string }> = [
  { value: 'ISSUED', label: 'เบิกออกแล้ว' },
  { value: 'PARTIALLY_RETURNED', label: 'คืนบางส่วน' },
  { value: 'RETURNED', label: 'คืนครบแล้ว' },
  { value: 'WRITTEN_OFF', label: 'ตัดจำหน่ายแล้ว' },
  { value: 'CANCELLED', label: 'ยกเลิก' },
];

export const STOCK_RETURN_CONDITIONS: ReadonlyArray<{ value: StockReturnCondition; label: string }> = [
  { value: 'GOOD', label: 'สภาพดี — กลับเข้าคลัง' },
  { value: 'DAMAGED', label: 'ชำรุด' },
  { value: 'LOST', label: 'สูญหาย' },
];

/** สถานะที่ใบยังถือของอยู่ (คืน/ตัดจำหน่ายได้) */
export const OPEN_ISSUE_STATUSES: ReadonlyArray<StockIssueStatus> = ['ISSUED', 'PARTIALLY_RETURNED'];

export const purposeLabel = (value: string | null | undefined): string => {
  if (!value) return '-';
  return STOCK_ISSUE_PURPOSES.find((o) => o.value === value)?.label ?? value;
};

export const statusLabel = (value: string | null | undefined): string => {
  if (!value) return '-';
  return STOCK_ISSUE_STATUSES.find((o) => o.value === value)?.label ?? value;
};

export const conditionLabel = (value: string | null | undefined): string | null => {
  if (!value) return null;
  return STOCK_RETURN_CONDITIONS.find((o) => o.value === value)?.label ?? value;
};

// ========== Rows ==========

/** ชนิดของรายการในใบเบิก — ยืมซิมได้ (feedback Support 23 ก.ย. 2026) */
export type StockIssueItemKind = 'DEVICE' | 'SIM';

export interface StockIssueItemRow {
  id: number;
  /** backend รุ่นเก่าไม่ส่ง = ถือเป็น DEVICE */
  item_kind?: StockIssueItemKind | null;
  device_id: number | null;
  /** snapshot มาก่อนค่าปัจจุบันเสมอ — อ่านประวัติได้แม้อุปกรณ์ถูกแก้/ลบ */
  device_code: string | null;
  device_name: string | null;
  device_serial: string | null;
  device_is_serialized: boolean | null;
  device_status: string | null;
  device_status_label: string | null;
  device_unit: string | null;

  /** แถวซิม (item_kind = 'SIM') — ฟิลด์ device_* เป็น null ทั้งหมด · quantity = 1 เสมอ */
  sim_id?: number | null;
  sim_phone?: string | null;
  sim_operator?: string | null;
  sim_status?: string | null;
  sim_status_label?: string | null;
  /** ป้ายที่ backend ประกอบให้ (ทั้งเครื่องและซิม) — ใช้ก่อนเสมอ */
  display_label?: string | null;

  quantity: number;
  /** ทยอยคืนได้ (สิ่งที่ equipment_return_items ไม่มี) */
  returned_quantity: number;
  /** ยังไม่ได้คืน — backend คำนวณให้ */
  outstanding_quantity: number;
  returned_date: string | null;
  condition_on_return: StockReturnCondition | string | null;
  condition_on_return_label: string | null;
  notes: string | null;
}

export interface StockIssueRow {
  id: number;
  issue_number: string;

  purpose: StockIssuePurpose | string;
  purpose_label: string;
  purpose_note: string | null;

  issued_to_user_id: number | null;
  issued_to_user_name: string | null;
  issued_to_name: string | null;
  /** ชื่อที่ควรโชว์บนจอ (user > ชื่ออิสระ) — backend ประกอบให้แล้ว */
  issued_to_display: string | null;
  issued_to_department: string | null;

  is_returnable: boolean;
  issue_date: string | null;
  expected_return_date: string | null;

  status: StockIssueStatus | string;
  status_label: string;

  rental_id: number | null;
  rental_number: string | null;

  reason: string | null;
  notes: string | null;

  issued_by_user_id: number | null;
  issued_by_name: string | null;

  created_at: string | null;
  updated_at: string | null;

  total_quantity: number;
  returned_quantity: number;
  outstanding_quantity: number;
  item_count: number;

  /** คำนวณฝั่ง backend เท่านั้น — **FE ห้ามคำนวณเอง** (กติกาเดียวกับ SLA มัดจำ) */
  is_overdue: boolean;
  days_overdue: number;

  items: StockIssueItemRow[];
}

export interface StockIssueListResponse {
  items: StockIssueRow[];
  total: number;
  page: number;
  page_size: number;
}

/** ทุก action หลังสร้างใบตอบรูปแบบนี้ (pattern เดียวกับ device_movements / deposit) */
export interface StockIssueActionResponse {
  issue: StockIssueRow;
  /** เตือนแต่ไม่บล็อค เช่น ของชำรุดไม่ได้กลับเข้ายอด */
  warnings: string[];
}

export interface PendingReturnsSummary {
  total_issues: number;
  overdue_issues: number;
  outstanding_quantity: number;
}

/** ของค้างคืน — หนึ่งแถว = หนึ่ง **ใบเบิก** (รายการที่ยังค้างอยู่ใน `items`) */
export interface PendingReturnsResponse {
  items: StockIssueRow[];
  total: number;
  page: number;
  page_size: number;
  /** ตัวเลขจากทั้งชุดที่ filter ได้ ไม่ใช่เฉพาะหน้าที่กำลังดู */
  summary: PendingReturnsSummary;
}

/** รายการในใบที่ยังไม่ได้คืน */
export const outstandingItems = (issue: StockIssueRow): StockIssueItemRow[] =>
  (issue.items || []).filter((it) => (it.outstanding_quantity || 0) > 0);

/** แถวนี้เป็นซิมหรือไม่ — ดู item_kind ก่อน ไม่มีค่อยดูว่ามี sim_id แต่ไม่มี device_id */
export const isSimItem = (item: Pick<StockIssueItemRow, 'item_kind' | 'sim_id' | 'device_id'>): boolean =>
  item.item_kind === 'SIM' || (!item.item_kind && !!item.sim_id && !item.device_id);

/** หน่วยของแถว — ซิม = "ใบ" · อุปกรณ์ = หน่วยของอุปกรณ์ (fallback ตามที่ผู้เรียกกำหนด) */
export const itemUnit = (item: StockIssueItemRow, fallback = ''): string =>
  isSimItem(item) ? 'ใบ' : item.device_unit || fallback;

/**
 * ซิมหลังคืน/ตัดจำหน่าย จะอยู่สถานะไหน (ตรงกับ backend — มติเจ้าของโปรเจกต์ 23 ก.ย. 2026)
 * คืน: สภาพดี → พร้อมใช้งาน · ชำรุด → ไม่ใช้งาน (INACTIVE) · สูญหาย → ระงับ (SUSPENDED)
 * ตัดจำหน่าย: → หมดอายุ (ยกเลิกเบอร์) ทุกสภาพ
 */
export const simConditionHint = (
  mode: 'return' | 'write-off',
  condition: StockReturnCondition | string
): string => {
  if (mode === 'write-off') return 'ซิมที่ตัดจำหน่าย → สถานะ "หมดอายุ" (ยกเลิกเบอร์)';
  if (condition === 'GOOD') return 'ซิมสภาพดี → กลับเป็น "พร้อมใช้งาน"';
  if (condition === 'DAMAGED') return 'ซิมชำรุด → สถานะ "ไม่ใช้งาน" (INACTIVE)';
  if (condition === 'LOST') return 'ซิมสูญหาย → สถานะ "ระงับ" (SUSPENDED)';
  return '';
};

/** ชื่อรายการแบบสั้นสำหรับตาราง — ป้ายจาก backend มาก่อน */
export const itemLabel = (item: StockIssueItemRow): string => {
  const fromBackend = (item.display_label || '').trim();
  if (fromBackend) return fromBackend;
  if (isSimItem(item)) {
    const phone = (item.sim_phone || '').trim();
    return phone ? `ซิม ${phone}` : `ซิม #${item.sim_id ?? item.id}`;
  }
  return [item.device_code, item.device_name].filter(Boolean).join(' · ') || `รายการ #${item.id}`;
};

/** ชื่อผู้เบิกที่แสดงบนจอ — ใช้ค่าที่ backend ประกอบมาก่อนเสมอ */
export const issuedToDisplay = (row: {
  issued_to_display?: string | null;
  issued_to_user_name?: string | null;
  issued_to_name?: string | null;
  issued_to_department?: string | null;
}): string => {
  if (row.issued_to_display) return row.issued_to_display;
  const who = row.issued_to_user_name || row.issued_to_name || 'ไม่ระบุ';
  return row.issued_to_department ? `${who} (${row.issued_to_department})` : who;
};

// ========== Picker: อุปกรณ์ที่เบิกได้ ==========

export interface AvailableDeviceRow {
  id: number;
  code: string | null;
  serial_number: string | null;
  name: string | null;
  device_type_id: number | null;
  device_type_name: string | null;
  brand_name: string | null;
  is_serialized: boolean;
  status: string | null;
  status_label: string | null;
  /** ของนับจำนวน = ยอดคงเหลือ · ของมี serial = 1 */
  quantity: number;
  unit: string | null;
  minimum_quantity: number;
  low_stock: boolean;
}

export interface AvailableDevicesResponse {
  items: AvailableDeviceRow[];
  total: number;
  page: number;
  page_size: number;
  // ค-2 (fix ค, 20 ก.ย. 2026): แถวที่ "พบแต่ใช้ไม่ได้" — เติมเฉพาะตอน search ไม่ว่าง + หน้าแรก
  // สูงสุด 5 แถว · optional เพราะ backend รุ่นเก่ายังไม่ส่ง
  blocked?: PickerBlockedItem[];
}

// ========== Picker: ซิมที่ยืมได้ ==========

export interface AvailableSimRow {
  id: number;
  phone_number: string | null;
  operator: string | null;
  sim_type: string | null;
  sim_type_label: string | null;
  status: string | null;
  status_label: string | null;
  expiry_date: string | null;
  package_name: string | null;
}

export interface AvailableSimsResponse {
  items: AvailableSimRow[];
  total: number;
  page: number;
  page_size: number;
  blocked?: PickerBlockedItem[];
}

// ========== Picker: ผู้เบิกที่เป็น user ในระบบ ==========

/**
 * B5 / R3R-8 (8 ก.ย. 2026): เดิมฟอร์มใบเบิกให้เลือก "ผู้เบิก" จากรายชื่อผ่าน `GET /users/`
 * ซึ่ง gate ด้วย `users.view` — Support บน prod ไม่มี → ทุกใบที่ Support สร้างมี
 * `issued_to_user_id` = NULL (พิมพ์ชื่อเอง) → แจ้งเตือนเกินกำหนดต่อ user ผูกไม่ได้
 * endpoint นี้ gate ด้วย `stock_issues.create/edit` (pattern เดียวกับ `/preparation-jobs/products`)
 * คืนเฉพาะ id + ชื่อ ไม่ใช่ข้อมูลผู้ใช้เต็ม
 */
export interface IssuableUserRow {
  id: number;
  full_name: string | null;
  username: string;
  email: string | null;
}

export interface IssuableUsersResponse {
  items: IssuableUserRow[];
}

export const issuableUserLabel = (u: IssuableUserRow): string =>
  (u.full_name || '').trim() || u.username || `ผู้ใช้ #${u.id}`;

// ========== Payloads ==========

/** ต้องมี device_id **หรือ** sim_id อย่างใดอย่างหนึ่ง (backend บังคับ) · ซิม quantity = 1 */
export interface StockIssueItemInput {
  device_id?: number;
  sim_id?: number;
  quantity: number;
  notes?: string | null;
}

export interface StockIssueCreateRequest {
  purpose: StockIssuePurpose;
  /** บังคับเมื่อ purpose = OTHER */
  purpose_note?: string | null;
  issued_to_user_id?: number | null;
  issued_to_name?: string | null;
  issued_to_department?: string | null;
  is_returnable: boolean;
  issue_date: string;
  expected_return_date?: string | null;
  /** อ้างอิงเฉย ๆ ไม่บังคับ — จุดที่ accessory_requests ตาย */
  rental_id?: number | null;
  reason: string;
  notes?: string | null;
  items: StockIssueItemInput[];
}

export interface StockIssueReturnItemInput {
  item_id: number;
  quantity: number;
  condition: StockReturnCondition;
  returned_date?: string | null;
}

export interface StockIssueReturnRequest {
  items: StockIssueReturnItemInput[];
  note?: string | null;
}

export interface StockIssueWriteOffItemInput {
  item_id: number;
  quantity: number;
  condition: StockReturnCondition;
}

export interface StockIssueWriteOffRequest {
  items: StockIssueWriteOffItemInput[];
  reason: string;
}

export interface StockIssueCancelRequest {
  reason: string;
}

// ========== Filters ==========

export interface StockIssueFilters {
  page?: number;
  page_size?: number;
  purpose?: StockIssuePurpose | '';
  status?: StockIssueStatus | '';
  is_returnable?: boolean;
  issued_to_user_id?: number;
  device_id?: number;
  sim_id?: number;
  rental_id?: number;
  /** ใบที่ผูกสัญญาของโครงการนี้ (FB-2026-0009 · หน้ารายละเอียดโครงการ) */
  site_id?: number;
  date_from?: string;
  date_to?: string;
  search?: string;
}

/** filter ชุดเดียวใช้ทั้ง list และ export (bug class export-filter-parity) */
const buildFilterParams = (
  filters: Omit<StockIssueFilters, 'page' | 'page_size'>
): Record<string, string | number | boolean> => {
  const params: Record<string, string | number | boolean> = {};
  if (filters.purpose) params.purpose = filters.purpose;
  if (filters.status) params.status = filters.status;
  if (typeof filters.is_returnable === 'boolean') params.is_returnable = filters.is_returnable;
  if (filters.issued_to_user_id) params.issued_to_user_id = filters.issued_to_user_id;
  if (filters.device_id) params.device_id = filters.device_id;
  if (filters.sim_id) params.sim_id = filters.sim_id;
  if (filters.rental_id) params.rental_id = filters.rental_id;
  if (filters.site_id) params.site_id = filters.site_id;
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;
  if (filters.search) params.search = filters.search;
  return params;
};

export interface PendingReturnFilters {
  page?: number;
  page_size?: number;
  overdue_only?: boolean;
  purpose?: StockIssuePurpose | '';
  issued_to_user_id?: number;
  device_id?: number;
  sim_id?: number;
  rental_id?: number;
  date_from?: string;
  date_to?: string;
  search?: string;
}

// ========== Service ==========

const stockIssueService = {
  /** รายการใบเบิก + ตัวกรอง */
  async getIssues(filters: StockIssueFilters = {}): Promise<StockIssueListResponse> {
    const params = {
      page: filters.page || 1,
      page_size: filters.page_size || 20,
      ...buildFilterParams(filters),
    };
    return ApiWithLogging.get<StockIssueListResponse>('/stock-issues/', { params });
  },

  async getIssue(id: number): Promise<StockIssueRow> {
    return ApiWithLogging.get<StockIssueRow>(`/stock-issues/${id}`);
  },

  /** สร้างใบเบิก — **ตัดสต็อกทันที** ไม่มีขั้นอนุมัติ */
  async createIssue(data: StockIssueCreateRequest): Promise<StockIssueRow> {
    return ApiWithLogging.post<StockIssueRow>(
      '/stock-issues/',
      data,
      {},
      {
        service: 'stockIssueService',
        action: 'create_stock_issue',
        entityType: 'stock_issue',
      }
    );
  },

  /** รับคืนของที่เบิก (ทยอยคืนได้) */
  async returnItems(id: number, data: StockIssueReturnRequest): Promise<StockIssueActionResponse> {
    return ApiWithLogging.post<StockIssueActionResponse>(
      `/stock-issues/${id}/return`,
      data,
      {},
      {
        service: 'stockIssueService',
        action: 'return_stock_issue',
        entityType: 'stock_issue',
        entityId: id,
      }
    );
  },

  /** ตัดจำหน่าย — ของไม่กลับเข้าคลัง */
  async writeOff(id: number, data: StockIssueWriteOffRequest): Promise<StockIssueActionResponse> {
    return ApiWithLogging.post<StockIssueActionResponse>(
      `/stock-issues/${id}/write-off`,
      data,
      {},
      {
        service: 'stockIssueService',
        action: 'write_off_stock_issue',
        entityType: 'stock_issue',
        entityId: id,
      }
    );
  },

  /** ยกเลิกใบ — คืนของกลับทั้งหมด (ทำได้เฉพาะยังไม่มีการคืน/ตัดจำหน่ายบางส่วน) */
  async cancelIssue(id: number, data: StockIssueCancelRequest): Promise<StockIssueActionResponse> {
    return ApiWithLogging.post<StockIssueActionResponse>(
      `/stock-issues/${id}/cancel`,
      data,
      {},
      {
        service: 'stockIssueService',
        action: 'cancel_stock_issue',
        entityType: 'stock_issue',
        entityId: id,
      }
    );
  },

  /** ของค้างคืน — backend กรองเฉพาะใบยืมที่ยังไม่ปิด และเรียงตามกำหนดคืนที่ใกล้ที่สุดให้แล้ว */
  async getPendingReturns(filters: PendingReturnFilters = {}): Promise<PendingReturnsResponse> {
    const params: Record<string, string | number | boolean> = {
      page: filters.page || 1,
      page_size: filters.page_size || 20,
    };
    if (filters.overdue_only) params.overdue_only = true;
    if (filters.purpose) params.purpose = filters.purpose;
    if (filters.issued_to_user_id) params.issued_to_user_id = filters.issued_to_user_id;
    if (filters.device_id) params.device_id = filters.device_id;
    if (filters.sim_id) params.sim_id = filters.sim_id;
    if (filters.rental_id) params.rental_id = filters.rental_id;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.search) params.search = filters.search;
    return ApiWithLogging.get<PendingReturnsResponse>('/stock-issues/pending-returns', { params });
  },

  /** ตัวเลือก + label ไทย (วัตถุประสงค์ / สถานะ / สภาพของ) */
  async getOptions(): Promise<StockIssueOptionsResponse> {
    return ApiWithLogging.get<StockIssueOptionsResponse>('/stock-issues/purposes');
  },

  /**
   * รายชื่อผู้ใช้ที่เลือกเป็น "ผู้เบิก" ได้ — gate ด้วยสิทธิ์ใบเบิก ไม่ใช่ `users.view` (B5)
   * ล้มเหลว (403/404/422 บน server เก่า) → ผู้เรียก fallback เป็นพิมพ์ชื่อเอง
   */
  async getIssuableUsers(
    options: { search?: string; limit?: number } = {}
  ): Promise<IssuableUsersResponse> {
    const params: Record<string, string | number> = { limit: options.limit || 100 };
    if (options.search) params.search = options.search;
    const res = await ApiWithLogging.get<IssuableUsersResponse | IssuableUserRow[]>(
      '/stock-issues/users',
      { params }
    );
    // รับได้ทั้ง {items:[…]} (spec) และ array ตรง ๆ — กัน contract เพี้ยนตอน backend ยัง iterate
    const items = Array.isArray(res) ? res : res?.items;
    if (!Array.isArray(items)) throw new Error('รูปแบบรายชื่อผู้ใช้ไม่ถูกต้อง');
    return { items };
  },

  /** picker อุปกรณ์ที่เบิกได้ (serial AVAILABLE + ของนับจำนวน quantity > 0, ไม่รวมของที่ถูกลบ) */
  async getAvailableDevices(
    options: {
      search?: string;
      page?: number;
      page_size?: number;
      device_type_id?: number;
      is_serialized?: boolean;
    } = {}
  ): Promise<AvailableDevicesResponse> {
    const params: Record<string, string | number | boolean> = {
      page: options.page || 1,
      page_size: options.page_size || 20,
    };
    if (options.search) params.search = options.search;
    if (options.device_type_id) params.device_type_id = options.device_type_id;
    if (typeof options.is_serialized === 'boolean') params.is_serialized = options.is_serialized;
    return ApiWithLogging.get<AvailableDevicesResponse>('/stock-issues/available-devices', {
      params,
    });
  },

  /** picker ซิมที่ยืมได้ (backend กรองสถานะ/ผู้ถือ/ใบตามเก็บให้แล้ว) */
  async getAvailableSims(
    options: { search?: string; page?: number; page_size?: number } = {}
  ): Promise<AvailableSimsResponse> {
    const params: Record<string, string | number> = {
      page: options.page || 1,
      page_size: options.page_size || 20,
    };
    if (options.search) params.search = options.search;
    return ApiWithLogging.get<AvailableSimsResponse>('/stock-issues/available-sims', { params });
  },

  /** Export — ส่ง filter ชุดเดียวกับหน้าจอทุกตัว */
  async exportToExcel(filters: Omit<StockIssueFilters, 'page' | 'page_size'> = {}): Promise<Blob> {
    return ApiWithLogging.get<Blob>(
      '/stock-issues/export/excel',
      { params: buildFilterParams(filters), responseType: 'blob' },
      {
        service: 'stockIssueService',
        action: 'export_stock_issues_excel',
        entityType: 'stock_issue',
      }
    );
  },
};

export default stockIssueService;
